import React, { memo, useCallback, useContext, useEffect } from 'react';

import type { InlineCommentViewComponentProps } from '@atlaskit/editor-plugins/annotation';

import { useCommentsContentState, useCommentsContentActions } from '@confluence/comment-context';
import { useAnnotations, useAnnotationsDispatch } from '@confluence/annotation-provider-store';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter';
import {
	ExperienceTrackerContext,
	ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
	VIEW_INLINE_COMMENT_EXPERIENCE,
	ExperienceTimeout,
	selectInlineComment,
} from '@confluence/experience-tracker';
import { fg } from '@confluence/feature-gating';
import { useShowCommentsPanel } from '@confluence/comments-panel';

import type { ViewComponentProps } from './ViewComponent';

const relativeFilePath = 'annotation-provider-inline-comments/editor/ViewManager';

type ViewManagerProps = ViewComponentProps & {
	newlySelectedAnnotationId?: string;
};

export const ViewManagerWithProps = ({
	pageId,
	editorActions,
	newlySelectedAnnotationId,
	annotations,
	annotationsList,
	getInlineNodeTypes,
	onClose,
	onDelete,
	onResolve,
	isOpeningMediaCommentFromToolbar,
	isOffline: isOfflineFromProps,
}: ViewManagerProps & InlineCommentViewComponentProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { editorViewProps, editorCreateProps } = useAnnotations();
	const { setEditorCreateProps, setEditorViewProps, setIsOffline } = useAnnotationsDispatch();
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const { showModal } = useDialogs();
	const emitter = getEditorAnnotationEventEmitter();
	const { isCommentsPanelShown } = useShowCommentsPanel();

	const handleClose = useCallback(() => {
		// NOTE: Any logic added to the handleClose function will need to be duplicated to EditorCommentShortcutListener.tsx. To revisit - create a shared function so the close inline comment logic can be reused
		// When closing the sidebar, return focus to the previously selected annotation element
		if (fg('confluence_frontend_editor_annotation_a11y_fix')) {
			setTimeout(() => {
				editorActions?.focus();
			}, 100);
		}

		onClose && onClose();
		setEditorViewProps(null);

		if (fg('confluence_frontend_view_inline_comment_editor')) {
			experienceTracker.abort({
				name: VIEW_INLINE_COMMENT_EXPERIENCE,
				reason: 'Inline comment was closed',
				checkForTimeout: true,
				publishDebugPoints: true,
				attributes: {
					stopPoint: relativeFilePath,
				},
			});
		}
	}, [editorActions, onClose, setEditorViewProps, experienceTracker]);

	useEffect(() => {
		const handleSetEditorViewProps = () => {
			setEditorViewProps({
				pageId,
				editorActions,
				annotations,
				annotationsList,
				getInlineNodeTypes,
				onClose: handleClose,
				onDelete,
				onResolve,
				isOpeningMediaCommentFromToolbar,
			});

			// Only start the experience if the comments panel is closed to track the core view-inline-comment experience
			if (
				!isCommentsPanelShown &&
				annotations[0]?.id &&
				fg('confluence_frontend_view_inline_comment_editor')
			) {
				const experienceState = experienceTracker.getExperienceState(
					VIEW_INLINE_COMMENT_EXPERIENCE,
				);

				/*
				 * Note: This logic runs after certain entrypoints start the view-inline-comment experience.
				 * We don't start a new experience if the annotation ID of the running experience is the same
				 * as the current annotation to avoid duplicating and aborting experiences.
				 */
				if (
					experienceState === null ||
					experienceState?.hasStopped ||
					(experienceState?.hasStopped === false &&
						experienceState?.attributes?.annotationId !== annotations[0]?.id)
				) {
					experienceTracker.start({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
						attributes: {
							annotationId: annotations[0]?.id,
							entryPoint: relativeFilePath,
							pageClass: 'editor',
						},
					});
				}
			}

			if (editorCreateProps) {
				if (!fg('confluence_frontend_create_to_view_transition')) {
					setEditorCreateProps(null);
				}
				experienceTracker.succeed({
					name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
				});
			}
		};

		// The annotation currently being shown
		const currentlyShownAnnotationId =
			editorViewProps && editorViewProps.annotations.length > 0
				? editorViewProps.annotations[0].id
				: undefined;

		// We only want to do anything if a new annotation has been clicked/selected
		if (newlySelectedAnnotationId !== currentlyShownAnnotationId) {
			// We only want to show the dialog if a new annotation has been selected AND content has changed
			// if a new annotation has not been selected, the onClose() function will handle the changed dialog

			// COMMENTS-3216 - With Concurrent Mode the rendering cycle is optimized to show things sooner
			//				   causing this component to mount before the state change for the comment content
			//				   is registered and thus causes the user to be prompted. We will never have a case
			//				   that currentlyShownAnnotationId is undefined AND there are changes, so this is safe
			if (hasContentChanged && currentlyShownAnnotationId !== undefined) {
				let abortedExperience = false;
				let abortedExperienceEntrypoint = '';

				if (fg('confluence_frontend_view_inline_comment_keyboard')) {
					const experienceState = experienceTracker.getExperienceState(
						VIEW_INLINE_COMMENT_EXPERIENCE,
					);

					/*
					 * If a keyboard shortcut or comment navigation is used to navigate when the content has changed,
					 * we abort and restart the experience on confirm because we can't predict if the user will
					 * confirm the navigation, or how long the user will take to confirm.
					 */
					if (
						experienceState !== null &&
						experienceState?.hasStopped === false &&
						(experienceState?.attributes?.entryPoint ===
							'comments-shortcuts/EditorCommentShortcutListener' ||
							experienceState?.attributes?.entryPoint ===
								'inline-comments-common/CommentNavigation')
					) {
						abortedExperienceEntrypoint = experienceState?.attributes?.entryPoint;
						experienceTracker.abort({
							name: VIEW_INLINE_COMMENT_EXPERIENCE,
							reason: 'Comment warning dialog displayed',
							checkForTimeout: true,
							publishDebugPoints: true,
							attributes: {
								stopPoint: relativeFilePath,
							},
						});

						abortedExperience = true;
					}
				}

				showModal(CommentWarningDialog, {
					onConfirm: () => {
						if (abortedExperience) {
							experienceTracker.start({
								name: VIEW_INLINE_COMMENT_EXPERIENCE,
								timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
								attributes: {
									annotationId: annotations.length && annotations[0].id,
									entryPoint: relativeFilePath,
									pageClass: 'editor',
								},
							});

							selectInlineComment.debug(
								`Restarting experience after comment was discarded from comment warning dialog. Initial entrypoint: ${abortedExperienceEntrypoint}`,
							);
						}

						// On confirm, activate the newly clicked one
						if (annotations.length > 0) {
							emitter.emit('setselectedannotation', annotations[0].id);
						}

						resetContentChanged();
						handleSetEditorViewProps();
					},
					onCancel: () => {
						// If we have a previously shown annotationId we want to keep it highlighted until the user makes their choice
						if (currentlyShownAnnotationId) {
							emitter.emit('setselectedannotation', currentlyShownAnnotationId);
						}
					},
				});
			} else {
				handleSetEditorViewProps();
			}
		}

		// We only want to listen to changes that signifies a new comment click
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [newlySelectedAnnotationId]);

	useEffect(() => {
		// This is effectively gated by the platform_editor_offline_editing_web experiment, via platform/packages/editor/editor-plugin-annotation/src/ui/InlineCommentView.tsx
		setIsOffline(isOfflineFromProps === true);
	}, [isOfflineFromProps, setIsOffline]);

	return null;
};

export const ViewManager = ({ pageId, editorActions }: ViewManagerProps) =>
	memo((viewComponentProps: InlineCommentViewComponentProps) => {
		// TODO: Handle multiple annotations
		const newlySelectedAnnotationId =
			viewComponentProps.annotations.length > 0 ? viewComponentProps.annotations[0].id : undefined;

		return (
			<ViewManagerWithProps
				pageId={pageId}
				editorActions={editorActions}
				newlySelectedAnnotationId={newlySelectedAnnotationId}
				{...viewComponentProps}
			/>
		);
	});
